import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Cookie, X, Settings } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [legalUrls, setLegalUrls] = useState({
    kvkk_url: "https://alcodot.com/kvkk",
    cookie_policy_url: "https://alcodot.com/cerez-politikasi"
  });
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    fetchLegalUrls();
    const consent = localStorage.getItem("alcodot_cookie_consent");
    if (!consent) {
      setTimeout(() => setShowBanner(true), 1500);
    }
  }, []);

  const fetchLegalUrls = async () => {
    try {
      const response = await axios.get(`${API}/legal-settings`);
      if (response.data) {
        setLegalUrls(response.data);
      }
    } catch (error) {
      console.error("Legal URLs error:", error);
    }
  };

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("alcodot_cookie_consent", JSON.stringify(consentData));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true, // Zorunlu çerezler her zaman aktif
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("alcodot_cookie_consent", JSON.stringify(consentData));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      necessary: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem("alcodot_cookie_consent", JSON.stringify(consentData));
    setShowBanner(false);
    setShowDetails(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Overlay */}
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 fade-in" data-testid="cookie-overlay"></div>

      {/* Cookie Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 slide-up" data-testid="cookie-banner">
        <div className="max-w-6xl mx-auto">
          <div className="glass-card-premium rounded-3xl p-6 sm:p-8 neon-border shadow-2xl">
            <div className="flex items-start gap-4 mb-6">
              {/* Icon */}
              <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg neon-glow">
                <Cookie className="w-6 h-6 text-white" />
              </div>

              {/* Content */}
              <div className="flex-1">
                <h3 className="text-xl sm:text-2xl font-black text-white mb-3">
                  Çerez Bildirimi
                </h3>
                <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-4">
                  Web sitemiz, kullanıcı deneyimini iyileştirmek ve hizmetlerimizi geliştirmek amacıyla çerezler kullanmaktadır. 
                  Zorunlu çerezler sitenin çalışması için gereklidir ve her zaman aktiftir. 
                  İsteğe bağlı çerezler için izninize ihtiyacımız var.
                </p>

                {showDetails && (
                  <div className="space-y-3 mb-6 bg-gray-900/30 p-4 rounded-xl border border-teal-500/20">
                    {/* Necessary Cookies */}
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-white font-bold text-sm">Zorunlu Çerezler</p>
                        <p className="text-gray-400 text-xs">Site işlevselliği için gerekli</p>
                      </div>
                      <div className="bg-teal-500 text-black text-xs font-bold px-3 py-1 rounded-full">
                        Her Zaman Aktif
                      </div>
                    </div>

                    {/* Analytics Cookies */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-white font-bold text-sm">Analitik Çerezler</p>
                        <p className="text-gray-400 text-xs">Site kullanımını analiz etmek için</p>
                      </div>
                      <button
                        onClick={() => setPreferences({ ...preferences, analytics: !preferences.analytics })}
                        className={`w-12 h-6 rounded-full transition-all ${
                          preferences.analytics ? "bg-teal-500" : "bg-gray-600"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                            preferences.analytics ? "translate-x-6" : "translate-x-0.5"
                          }`}
                        ></div>
                      </button>
                    </div>

                    {/* Marketing Cookies */}
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <p className="text-white font-bold text-sm">Pazarlama Çerezleri</p>
                        <p className="text-gray-400 text-xs">Kişiselleştirilmiş reklamlar için</p>
                      </div>
                      <button
                        onClick={() => setPreferences({ ...preferences, marketing: !preferences.marketing })}
                        className={`w-12 h-6 rounded-full transition-all ${
                          preferences.marketing ? "bg-teal-500" : "bg-gray-600"
                        }`}
                      >
                        <div
                          className={`w-5 h-5 bg-white rounded-full shadow-md transition-transform ${
                            preferences.marketing ? "translate-x-6" : "translate-x-0.5"
                          }`}
                        ></div>
                      </button>
                    </div>
                  </div>
                )}

                {/* Links */}
                <div className="flex flex-wrap gap-4 mb-4 text-xs sm:text-sm">
                  <a
                    href={legalUrls.cookie_policy_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-400 hover:text-teal-300 underline"
                  >
                    Çerez Politikası
                  </a>
                  <a
                    href={legalUrls.kvkk_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-400 hover:text-teal-300 underline"
                  >
                    KVKK Aydınlatma Metni
                  </a>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={handleRejectAll}
                className="flex-shrink-0 text-gray-400 hover:text-white smooth-transition"
                data-testid="cookie-close-btn"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              {!showDetails && (
                <Button
                  onClick={() => setShowDetails(true)}
                  variant="outline"
                  className="flex-1 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500 hover:bg-teal-500/10 font-bold h-12"
                  data-testid="cookie-customize-btn"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Özelleştir
                </Button>
              )}

              {showDetails && (
                <Button
                  onClick={handleSavePreferences}
                  variant="outline"
                  className="flex-1 rounded-xl bg-gray-900/50 border-2 border-teal-500/30 text-teal-400 hover:border-teal-500 hover:bg-teal-500/10 font-bold h-12"
                  data-testid="cookie-save-btn"
                >
                  Tercihleri Kaydet
                </Button>
              )}

              <Button
                onClick={handleRejectAll}
                variant="outline"
                className="flex-1 rounded-xl bg-gray-900/50 border-2 border-gray-600 text-gray-300 hover:border-gray-500 hover:bg-gray-800/50 font-semibold h-12"
                data-testid="cookie-reject-btn"
              >
                Sadece Zorunlu
              </Button>

              <Button
                onClick={handleAcceptAll}
                className="flex-1 sm:flex-[1.5] rounded-xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-400 hover:to-cyan-500 text-black font-black h-12 shadow-lg neon-glow"
                data-testid="cookie-accept-btn"
              >
                Tümünü Kabul Et
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
